import React from 'react';
import {concepts, Group, useDelayedState, User, StripeProduct, SignUpCode} from 'fearless-common';
import {adminRoutes} from "../../utils/routes";
import Pages from "../../components/layout/Pages";
import OrderableTable from "../../components/layout/OrderableTable";
import OsIcon from "../../components/OSIcon";
import moment from "moment/moment";
import {defaultDateTimeFormat} from "../../utils/date";
import SearchInput from "../../components/SearchInput";
import useTitle from "../../components/hooks/useTitle";
import {ColumnDef} from "@tanstack/react-table";
import {getProductLabel} from "../../libs/productLabel";
import {Button, getFullName} from 'fearless-common-ui';
import {PlusLg} from "react-bootstrap-icons";
import StandardIcons from "../../components/layout/StandardIcons";
import {Badge, OverlayTrigger, Tooltip} from "react-bootstrap";
import CellRender from "../../components/layout/table/CellRender";
import {backendEndpoints} from "../../libs/backendEndpoints";

interface ReferralLink {
    source: string;
    code: string;
    user: User;
}

type UserWithData = User & { groups: Group[]; sign_up_code?: SignUpCode; referral_link?: ReferralLink }

const columns: ColumnDef<UserWithData>[] = [
    { id: 'id', accessorKey: 'id', header: 'ID', enableSorting: true },
    { id: 'name', accessorKey: 'first_name', header: 'Name', cell: CellRender.FullName, enableSorting: true, },
    { id: 'organisation', accessorKey: 'organisation', header: 'Email, Position, Group, Organisation', cell: ({ row }) => (
        <small>
            <div>{row.original.email}</div>
            {[row.original?.position, row.original?.group_name, row.original?.organisation].filter(v => v).join(', ')}
        </small>
    ), enableSorting: false,
    },
    { id: 'subscription', accessorKey: 'subscription_per_product', header: 'Subscribed to', cell: ({ row }) => (<>
            {[
                row.original.subscription_per_product?.photo51 && <StandardIcons.Photo51 width={25} />,
                row.original.subscription_per_product?.eln && <StandardIcons.Eln width={25} />,
                row.original.subscription_per_product?.lims && getProductLabel(StripeProduct.lims),
            ].filter((v) => v).map(v => <span className="me-2">{v}</span>)}
        </>), enableSorting: false,
    },
    { id: 'os', accessorKey: 'last_desktop_access_token', header: 'Last Desktop OS', cell: ({ row }) => {
        return (
                <>
                    {row.original.last_desktop_access_token?.length
                        ? (
                            <span className="text-sm">
                                <OsIcon name={row.original.last_desktop_access_token[0].name} />
                                {moment(row.original.last_desktop_access_token[0].last_used_at).format(defaultDateTimeFormat)}
                            </span>
                        )
                        : ''}
                    <div className="text-sm">{row.original.last_version_used}</div>
                </>
            )
        } },
    { id: 'trial', accessorKey: 'on_trial', header: 'Trial', cell: CellRender.Bool, enableSorting: true, },
    { id: 'groups', accessorKey: 'groups', header: '# of groups', cell: CellRender.Length, enableSorting: true, },
    { id: 'created_at', accessorKey: 'created_at', header: 'Signed up', cell: CellRender.DateTime, enableSorting: true, },
    { id: 'email_verified', accessorKey: 'email_verified_at', header: 'Email verified', cell: CellRender.Bool, enableSorting: true, },
    {
        id: 'extra',
        accessorKey: 'sign_up_code',
        header: 'Extra',
        enableSorting: false,
        cell: ({ row }) => (
          <>
              {row.original.sign_up_code && (
                <OverlayTrigger overlay={<Tooltip>Signed up using this {concepts.SignUpCode.toLowerCase()}</Tooltip>}>
                    <Badge>{row.original.sign_up_code.code}</Badge>
                </OverlayTrigger>
              )}
              {row.original.referral_link?.user && (
                <OverlayTrigger overlay={<Tooltip>Signed up using a referral link from this user</Tooltip>}>
                    <Badge>Through {getFullName(row.original.referral_link.user)}</Badge>
                </OverlayTrigger>
              )}
          </>
        ),
    },
]

/**
 * Shows a table of users in the Admin environment.
 */
export default function AdminUsers() {
    const [query, setQuery] = useDelayedState('');

    useTitle(`Admin ${concepts.Users}`);

    return (
        <Pages.Base padded className="px-3">
            <div className="d-flex align-items-center">
                <h1 className="mb-0 me-3">Users</h1>
                <div className="flex-grow-1">
                    <Button href={adminRoutes.addUser}>
                        <PlusLg /> add user
                    </Button>
                </div>
                <div className="ms-3">
                    <SearchInput
                      onChange={(e) => {
                          // @ts-ignore
                          setQuery(e.target.value);
                      }}
                    />
                </div>
            </div>
            <OrderableTable
                filterables={[
                    {
                        field_name: 'deleted_at',
                        label: 'Archived',
                        options: [
                            { value: 'null', label: 'Active' },
                            { value: 'not null', label: 'Archived' },
                        ],
                    },
                    {
                        field_name: 'email_verified_at',
                        label: 'Email verified',
                        options: [
                            { value: 'not null', label: 'Verified' },
                            { value: 'null', label: 'Not verified' },
                        ],
                    },
                    {
                        field_name: 'sign_up_code_id',
                        label: 'Used sign up code',
                        options: [
                            { value: 'not null', label: 'Yes' },
                            { value: 'null', label: 'No' },
                        ],
                    },
                    {
                        field_name: 'referral_link_id',
                        label: 'Via referral',
                        options: [
                            { value: 'not null', label: 'Yes' },
                            { value: 'null', label: 'No' },
                        ],
                    },
                ]}
                baseEndpoint={`${backendEndpoints.getAdminUsers}?_orderBy=created_at&_with[]=lastDesktopAccessToken&_with[]=signUpCode&_with[]=referralLink.user`}
                memoisedColumns={columns}
                buildLink={(record) => adminRoutes.user(record.id)}
                pagination
                className="table-sm"
                // @ts-ignore
                searchQuery={query}
            />
        </Pages.Base>
    );
}
